import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Gavel, Crown, ArrowRight } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      navigate("/feed");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-[#D9D4CC] px-4 py-3 text-[#2C2A29] placeholder-[#A39C90] rounded-sm focus:ring-2 focus:ring-[#EAE6DF] focus:border-[#B7AE9F] focus:outline-none transition-all duration-300";

  return (
    <div className="max-w-md mx-auto px-6 sm:px-8 pt-16 sm:pt-24 pb-24">
      <div className="text-center mb-10 animate-fade-up">
        <div className="inline-flex items-center justify-center w-14 h-14 border border-[#D9D4CC] bg-[#FDFBF7] rounded-sm mb-6">
          <Gavel className="w-6 h-6 text-[#2C2A29]" strokeWidth={1.5} />
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl text-[#2C2A29] tracking-tight">
          {mode === "login" ? "Welcome back" : "Create your account"}
        </h1>
        <p className="font-body text-[#7A756E] mt-3 text-sm">
          {mode === "login"
            ? "Sign in to generate and revisit your decrees."
            : "Save your decrees and build your arsenal of declarations."}
        </p>
      </div>

      <form onSubmit={submit} className="space-y-4 animate-fade-up" style={{ animationDelay: "0.1s" }}>
        {mode === "register" && (
          <input
            data-testid="name-input"
            className={inputCls}
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        )}
        <input
          data-testid="email-input"
          type="email"
          className={inputCls}
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <input
          data-testid="password-input"
          type="password"
          className={inputCls}
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={6}
        />

        {error && (
          <p data-testid="auth-error" className="text-sm text-[#9B3B3B] font-body">{error}</p>
        )}

        <button
          data-testid="auth-submit-button"
          type="submit"
          disabled={busy}
          className="w-full px-6 py-3.5 bg-[#2C2A29] text-[#FAF9F6] rounded-sm hover:opacity-90 transition-opacity duration-300 disabled:opacity-50"
        >
          {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
        </button>
      </form>

      <div className="flex items-center gap-4 my-7">
        <div className="flex-1 h-px bg-[#D9D4CC]" />
        <span className="font-body text-xs uppercase tracking-widest text-[#A39C90]">or</span>
        <div className="flex-1 h-px bg-[#D9D4CC]" />
      </div>

      <button
        data-testid="google-signin-button"
        onClick={() => {
          // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
          const redirectUrl = window.location.origin + "/generate";
          window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
        }}
        className="w-full flex items-center justify-center gap-3 px-6 py-3.5 border border-[#D9D4CC] text-[#2C2A29] rounded-sm hover:bg-[#EAE6DF] transition-colors duration-300"
      >
        <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
        Continue with Google
      </button>

      <p className="text-center text-sm text-[#7A756E] mt-8 font-body">
        {mode === "login" ? "New here?" : "Already have an account?"}{" "}
        <button
          data-testid="toggle-auth-mode"
          onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
          className="text-[#2C2A29] underline underline-offset-4 hover:opacity-70 transition-opacity"
        >
          {mode === "login" ? "Create an account" : "Sign in"}
        </button>
      </p>

      <div
        data-testid="auth-pricing-card"
        className="mt-10 border border-[#EAE6DF] bg-[#FDFBF7] rounded-sm px-5 py-4 animate-fade-up"
        style={{ animationDelay: "0.2s" }}
      >
        <p className="font-body text-sm text-[#4A4744]">
          <span className="font-semibold text-[#2C2A29]">Creating an account is free</span> — full decree sets,
          listening, all 9 Bible translations, plans, and the Newsfeed.
        </p>
        <p className="font-body text-sm text-[#4A4744] mt-2 flex items-start gap-1.5">
          <Crown className="w-4 h-4 text-[#9B7E4E] shrink-0 mt-0.5" strokeWidth={1.75} />
          <span>
            Premium is optional: <span className="text-[#2C2A29] font-medium">$4.99/mo or $49.99/yr</span> after a
            7-day free trial.
          </span>
        </p>
        <Link
          to="/premium"
          data-testid="auth-pricing-link"
          className="inline-flex items-center gap-1 font-body text-sm text-[#9B7E4E] underline underline-offset-4 hover:text-[#2C2A29] transition-colors duration-300 mt-2.5"
        >
          Compare plans and pricing <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}
